<script setup lang="ts">
import { computed } from 'vue';
import { BaseButton, FieldSelect } from '@magma-app/magma-lapilli';
import {
  getCriteriaOptions,
  type CampaignCriteria,
  type CriteriaAnswers,
} from '@/composables/helpee-signup/useHelpeeCriteria';

const props = defineProps<{
  criteriaList: CampaignCriteria[];
  answers: CriteriaAnswers;
  canSubmit: boolean;
  primaryColor?: string;
  rgbColor?: string;
  isLoading?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:answers', value: CriteriaAnswers): void;
  (e: 'submit'): void;
  (e: 'criteria-change', index: number): void;
}>();

const answersMap = computed(() => props.answers);

const updateAnswer = (criteriaId: number, value: string | string[]) => {
  emit('update:answers', {
    ...props.answers,
    [String(criteriaId)]: value,
  });
};

const onSubmit = () => {
  if (!props.canSubmit || props.isLoading) {
    return;
  }

  emit('submit');
};

const onCriteriaChange = (index: number) => {
  emit('criteria-change', index);
};
</script>

<template>
  <div>
    <h3 class="text-mgm-txt-lg font-medium text-foreground-emphasis mb-4">
      {{ $t('helpeeCampaignSignUpView.moreDetails') }}
    </h3>

    <FieldSelect
      v-for="(criteriaItem, criteriaIndex) in criteriaList"
      :id="criteriaItem.id"
      :key="criteriaItem.id"
      :model-value="answersMap[String(criteriaItem.id)]"
      class="mt-4"
      :custom-color="primaryColor"
      :custom-rgb-color="rgbColor || primaryColor"
      :label="criteriaItem.label"
      :options="getCriteriaOptions(criteriaItem)"
      :multiple="criteriaItem.type === 'multiSelect'"
      @update:model-value="(value) => updateAnswer(criteriaItem.id, value)"
      @change="onCriteriaChange(criteriaIndex)"
    />

    <BaseButton
      data-cy="signup-next"
      class="mt-8 w-full"
      :disabled="!canSubmit || isLoading"
      :is-loading="isLoading"
      :text="$t('generic.next')"
      size="md"
      :custom-color="primaryColor"
      :custom-rgb-color="rgbColor || primaryColor"
      @click="onSubmit"
    />
  </div>
</template>
